/* eslint-disable import/no-internal-modules */
import { PopoverProps } from 'antd';
import { differenceBy, isEqual } from 'lodash';
import React, {
  FC,
  MutableRefObject,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { FormattedMessage } from 'react-intl';
import { useDispatch } from 'react-redux';
import { OptionTypeBase } from 'react-select';
import styled from 'styled-components';
import useDeepCompareEffect from 'use-deep-compare-effect';
import { MemberType, ProjectId, Team } from 'api/models';
import { EventSource } from 'api/project';
import { Button } from 'components/atoms/button';
import { OutsideClickHandler } from 'components/atoms/outside-click-handler';
import { Popover } from 'components/atoms/popover';
import { SelectProps } from 'components/atoms/select';
import { ClickEventTags, setMarkerForDataDog } from 'hooks/use-data-dog-logger';
import { useIsRestrictedPartnerUser } from 'hooks/use-partner';
import { queryClient } from 'queries';
import {
  CreatePlaceholderPayloadOpts,
  createProjectPlaceholderMutationFn,
  getPlaceholderResolutionByProjectQueryKeyV2,
  PlaceholderResolutionV2,
  usePlaceholderResolutionByProjectQueryV2,
  useResolveTeamInLocalState,
} from 'queries/placeholder';
import { addProjectPlaceholder } from 'stores/memberships';
import { isChildNode } from 'utils/is-child-node';
import { Option } from '../squad-picker/types';
import { teamOption } from '../squad-picker/utils';
import { DefaultTrigger } from './default-trigger';
import { PlaceholderCreate } from './placeholder-create';
import { AssignSuccessData } from './placeholder-section';
import { AssigneePickerSelect } from './select';
import { useAssignTeamsToProject } from './use-assign-teams-to-project';

const ASSIGNEE_PICKER_HOSTED_COMPONENT_CLASS =
  'assignee-picker-hosted-component';

export interface BaseProps {
  title?: string | React.ReactNode;
  hideTrigger?: boolean;
  hidePlaceholderSection?: boolean;
  hidePlaceholderCreate?: boolean;
  containerRef?: MutableRefObject<any>;
  selectProps?: SelectProps<OptionTypeBase, true>;
  popoverProps?: Partial<PopoverProps>;
  onDone?: (options, callback?) => void;
  onSave?: (options, callback?) => void;
  onClose?: () => void;
  projectId?: ProjectId;
  isMini?: boolean;
  isPrivate?: boolean;
  value?: Option[];
  triggerComponent?: React.ReactElement;
  userInviteHandler?: (options, added?) => Promise<Option[]>;
  preventSubmitOnDismiss?: boolean;
  responsibleTeam?: MemberType[];
  onPlaceholderCreate?: (team) => void;
  placeholderCreateOptions?: CreatePlaceholderPayloadOpts;
  readOnly?: boolean;
  dueDate?: string | null;
  startDate?: string | null;
  hideHoverBgOnButtonWrapper?: boolean;
  isDisabled?: boolean;
}

interface ContentProps {
  onChange: (values) => void;
  onPlaceholderCreate: (placeholder) => void;
  onPlaceholderAssign: (placeholders, callback) => void;
  onPlaceholderAssignSuccess: (data: AssignSuccessData) => void;
  selected?: Option[];
  clearSelection: () => void;
}

const StyledAssigneePickerSelect = styled(AssigneePickerSelect)``;

const StyledPopover = styled(Popover)`
  &.ant-popover {
    width: 296px;
    height: 448px;
  }
  .ant-popover-content,
  .ant-popover-inner,
  .ant-popover-inner-content {
    height: 100%;
  }
  .ant-popover-inner-content,
  .ozone-select-container {
    display: flex;
    flex-flow: column;
  }
  .ozone-select-container {
    flex: 1;
    overflow: hidden;
    margin-bottom: var(--spacing-05);
  }
  ${StyledAssigneePickerSelect} {
    padding-top: var(--spacing-05);
  }

  .ant-btn-tertiary {
    margin-bottom: var(--spacing-03);
    margin-left: var(--spacing-03);
    margin-right: var(--spacing-03);
    justify-content: center;
  }
`;

const Title = styled.div`
  padding: var(--spacing-05);
  padding-bottom: 0;
  font-weight: var(--font-weight-bold);
`;

const Content = ({
  title,
  hidePlaceholderCreate,
  onDone,
  selectProps,
  projectId,
  onChange,
  responsibleTeam,
  selected,
  isPrivate,
  clearSelection,
  placeholderCreateOptions,
  onPlaceholderCreate,
  dueDate,
  startDate,
  isDisabled,
}: BaseProps & ContentProps) => {
  const [placeholderResolutions, setPlaceholderResolutions] =
    useState<PlaceholderResolutionV2>();

  // this query/ API is applicable only for a project and is ensured via enabled option of react query.
  // in case of missing project id, dummy project id of -1 is passed just to prevent ts error.
  const { data, isLoading: isLoadingPlaceholderResolutions } =
    usePlaceholderResolutionByProjectQueryV2(projectId ?? ProjectId(-1), {
      enabled: !!projectId,
    });

  useEffect(() => {
    if (!isLoadingPlaceholderResolutions) {
      setPlaceholderResolutions(data);
    }
  }, [data, isLoadingPlaceholderResolutions]);

  const [showPlaceholderCreateModal, setShowPlaceholderCreateModal] =
    useState(false);

  const projectPlaceholders = useMemo(
    () =>
      selectProps?.options?.find((_option) => _option.id === 'teams')?.options,
    [selectProps?.options],
  );

  const options = useMemo(
    () => [
      {
        label: 'Assignees',
        id: 'assignees',
        options: selected,
      },
      ...selectProps?.options,
    ],
    [selectProps?.options, selected],
  );

  const _onPlaceholderCreate = useCallback(
    (_placeholder) => {
      setShowPlaceholderCreateModal(false);
      onPlaceholderCreate(_placeholder);
    },
    [onPlaceholderCreate],
  );

  const onPlaceholderCreateClick = useCallback(
    () => setShowPlaceholderCreateModal(true),
    [],
  );

  return (
    <>
      {title ? <Title>{title}</Title> : null}
      <StyledAssigneePickerSelect
        hidePlaceholderCreate={hidePlaceholderCreate}
        clearSelection={clearSelection}
        {...selectProps}
        onChange={onChange}
        value={selected}
        options={options}
        dueDate={dueDate}
        startDate={startDate}
        onPlaceholderCreateClick={onPlaceholderCreateClick}
        placeholderResolutions={placeholderResolutions}
        isLoadingPlaceholderResolutions={isLoadingPlaceholderResolutions}
        isDisabled={isDisabled}
      />
      <Button
        type="tertiary"
        onClick={onDone}
        data-cy="assignee.picker.popup.done"
      >
        <FormattedMessage id="done" />
      </Button>
      {showPlaceholderCreateModal && (
        <PlaceholderCreate
          hostClassName={ASSIGNEE_PICKER_HOSTED_COMPONENT_CLASS}
          handler={
            projectId
              ? createProjectPlaceholderMutationFn(
                  projectId[1],
                  EventSource.PlaceholderManage,
                )
              : undefined
          }
          placeholders={projectPlaceholders}
          responsibleTeam={responsibleTeam}
          onSuccess={_onPlaceholderCreate}
          onCancel={() => setShowPlaceholderCreateModal(false)}
          isPrivate={isPrivate}
          options={placeholderCreateOptions}
        />
      )}
    </>
  );
};

export const AssigneePicker: FC<BaseProps> = (props) => {
  const {
    hideTrigger,
    isMini,
    containerRef,
    triggerComponent,
    selectProps,
    onDone,
    onSave,
    onClose,
    projectId,
    value,
    userInviteHandler,
    popoverProps,
    preventSubmitOnDismiss,
    onPlaceholderCreate,
    readOnly,
    hidePlaceholderCreate,
    hideHoverBgOnButtonWrapper,
    isDisabled,
  } = props;
  const dispatch = useDispatch();
  const [visible, _setVisible] = useState(hideTrigger);
  const setVisible = useCallback(
    (visibility) => _setVisible(readOnly ? false : visibility),
    [readOnly],
  );
  const isRestrictedPartnerUser = useIsRestrictedPartnerUser();
  const _hidePlaceholderCreate =
    hidePlaceholderCreate || isRestrictedPartnerUser;
  /**
   * initialValue is used to preserve the value passed in `value` props
   * when the assignee picker is made visible
   * to identify if there is any changeset on submit which happens on dismiss
   */
  const [initialValue, setInitialValue] =
    useState<Option[] | undefined | null>(value);
  const [selected, setSelected] = useState(value);
  const { addTeamsToProject } = useAssignTeamsToProject();

  useEffect(() => {
    if (visible && initialValue === null) {
      setInitialValue(value);
    }
    if (!visible && initialValue !== null) {
      setInitialValue(null);
    }
  }, [initialValue, value, visible]);

  useDeepCompareEffect(() => {
    setSelected(value);
  }, [value]);

  const clearSelection = useCallback(() => {
    setSelected([]);
    selectProps?.onChange?.([]);
  }, [selectProps]);

  const _onChange = useCallback(
    (_value) => {
      setSelected(_value);
      selectProps?.onChange?.(_value);
    },
    [selectProps],
  );

  const close = useCallback(() => {
    setVisible(false);
    onClose?.();
  }, [onClose, setVisible]);

  const submit = useCallback(async () => {
    if (isEqual(selected, initialValue)) {
      return;
    }
    if (projectId != null) {
      addTeamsToProject(projectId, selected ?? []);
    }

    if (!userInviteHandler) {
      onDone?.(selected);

      return;
    }
    const result = await userInviteHandler(
      selected ?? [],
      differenceBy(selected, initialValue ?? [], (x) => x.value),
    );

    setSelected(result);

    onDone?.(result);
  }, [
    addTeamsToProject,
    onDone,
    initialValue,
    projectId,
    selected,
    userInviteHandler,
  ]);

  const onOutsideClick = useCallback(() => {
    if (!visible) {
      return;
    }
    close();
    if (preventSubmitOnDismiss) {
      return;
    }
    submit();
  }, [close, preventSubmitOnDismiss, submit, visible]);

  const _onDone = useCallback(async () => {
    close();
    submit();
  }, [close, submit]);

  const _onPlaceholderCreate = useCallback(
    async (team: Team) => {
      if (onPlaceholderCreate) {
        onPlaceholderCreate(team);
      }

      const option = teamOption(team);
      const result = [...(selected || []), option];

      _onChange(result);
      if (!projectId) {
        return;
      }
      dispatch(
        addProjectPlaceholder({
          projectId,
          team,
        }),
      );
    },
    [_onChange, dispatch, onPlaceholderCreate, projectId, selected],
  );

  const onPlaceholderAssign = useCallback(
    (_, callback) => {
      onSave?.([...(selected || [])], callback);
    },
    [onSave, selected],
  );

  const refetchPlaceholderResolutions = useCallback(() => {
    if (projectId) {
      queryClient.refetchQueries(
        getPlaceholderResolutionByProjectQueryKeyV2(projectId),
      );
    }
  }, [projectId]);
  const resolveTeamInLocalState = useResolveTeamInLocalState();

  const onPlaceholderAssignSuccess = useCallback(
    (data: AssignSuccessData) => {
      refetchPlaceholderResolutions();
      let _selected = selected || [];
      const selectedUserIds = _selected
        .filter((_option) => _option.extra.type === 'user')
        .map((_option) => _option.extra.id);

      const _userOptions = selectProps?.options.find(
        (_option) => _option.id === 'users',
      )?.options;

      const notInProjectOptions = selectProps?.options.find(
        (_option) => _option.id === 'users_not_in_project',
      )?.options;

      const userOptions = [..._userOptions, ...notInProjectOptions];

      data.forEach((_team) => {
        const { placeholderId, userId } = _team;

        if (projectId != null) {
          resolveTeamInLocalState(projectId[1], placeholderId, userId);
        }

        _selected = _selected.filter(
          (_option) => _option.extra.id !== placeholderId,
        );

        if (selectedUserIds.includes(userId)) {
          return;
        }
        const user = userOptions?.find(
          (_userOpts) => _userOpts.extra.id === _team.userId,
        );

        _selected = [..._selected, user];
      });

      _onChange(_selected);
      close();
    },
    [
      _onChange,
      close,
      projectId,
      refetchPlaceholderResolutions,
      resolveTeamInLocalState,
      selectProps?.options,
      selected,
    ],
  );

  const isChild = isChildNode([
    ASSIGNEE_PICKER_HOSTED_COMPONENT_CLASS,
    'ant-modal-confirm', // to allow user invite confirm modals
  ]);

  return (
    <OutsideClickHandler allowed={isChild} onOutsideClick={onOutsideClick}>
      <StyledPopover
        isDisabled={isDisabled}
        className={ASSIGNEE_PICKER_HOSTED_COMPONENT_CLASS}
        visible={visible}
        content={
          <Content
            {...props}
            onChange={_onChange}
            onDone={_onDone}
            selected={selected}
            clearSelection={clearSelection}
            onPlaceholderCreate={_onPlaceholderCreate}
            onPlaceholderAssign={onPlaceholderAssign}
            onPlaceholderAssignSuccess={onPlaceholderAssignSuccess}
            hidePlaceholderCreate={_hidePlaceholderCreate}
          />
        }
        trigger="click"
        placement="bottomLeft"
        {...(containerRef && {
          getPopupContainer: () => containerRef?.current,
        })}
        {...popoverProps}
      >
        {hideTrigger ? null : triggerComponent ? (
          <Button
            type="text"
            onClick={() => {
              if (!visible) {
                setMarkerForDataDog(ClickEventTags.AssigneePickerPopup);
              }
              setVisible(!visible);
            }}
            hideHoverEffect={hideHoverBgOnButtonWrapper}
          >
            {triggerComponent}
          </Button>
        ) : (
          <DefaultTrigger
            value={selected}
            isMini={isMini}
            onClick={() => {
              if (!visible) {
                setMarkerForDataDog(ClickEventTags.AssigneePickerPopup);
              }
              setVisible(!visible);
            }}
          />
        )}
      </StyledPopover>
    </OutsideClickHandler>
  );
};
